/**
 * The payer list behind the บริษัทประกัน picker on /reports/claim-imports.
 *
 * ⚠️ THIS IS A MIRROR OF `ADAPTERS` IN functions/lib/insurerAdapters.js, AND A PAYER
 * MISSING FROM IT IS INVISIBLE WITH NOTHING FAILING ANYWHERE. src/ and functions/ are
 * separate bundles — the repo's stated convention for shared logic is duplicate-and-annotate,
 * not cross-import (functions/api/_conflict.js:40-43, _middleware.js:84) — so the mirror
 * cannot be removed. What it CAN have is a test: claimImportPayers.test.mjs imports this
 * file and the real registry and fails when they drift.
 *
 * That test exists because the drift already happened. Prudential shipped end-to-end on
 * 2026-08-27 — adapter, OLE2 reader, courier routing, all four suites green, proven on real
 * mail — and was still absent from the dropdown, because every one of those green checks
 * was on the functions/ side of the bundle split. The only symptom was a person looking at
 * a screen and not finding a payer that the server had been ready to accept for a day.
 *
 * It is extracted out of ClaimImports.jsx for exactly one reason: node --test cannot parse
 * JSX, so a list living in a .jsx file cannot be asserted against anything.
 */

// ⚠️ `accept` and `hint` are per payer because the FORMAT is per payer. AIA never emails a
// settlement — staff sign in to healthcare.aia.co.th and print the advice to PDF — so it is
// the only payer whose file is not a spreadsheet, and the only one the email courier can
// never produce a run for.
// ⚠️ `payer` IS THE `billing.advancePay` STRING AND IT IS NOT DERIVABLE FROM `key`. The
// history filter sends it upstream, and it used to be computed as
// `key === 'axa' ? 'AXA' : 'Generali'` — a binary ternary that silently filed AIA (and
// would have filed GoWabi) under Generali, so picking either in ทุกบริษัท returned
// Generali's runs. Keep this column in step with `ADAPTERS[].payer` in
// functions/lib/insurerAdapters.js; src/ and functions/ are separate bundles, so this list
// is a hand-maintained mirror rather than an import.
export const XLSX = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
// ⚠️ `.xls` IS NOT A TYPO FOR `.xlsx`, AND THE MIME TYPE IS NOT THE OOXML ONE. Prudential's
// attachment is a Crystal Reports export in the pre-2007 OLE2/BIFF8 binary format — a
// genuinely different container, read by functions/lib/insurerXls.js rather than by
// insurerXlsx.js. Listing XLSX here would make the file picker grey out the only file the
// operator has. Both spellings of the legacy type are accepted because the OS supplies the
// MIME and Windows and macOS do not agree on it; the extension is what actually filters.
export const XLS = '.xls,application/vnd.ms-excel,application/x-msexcel'
export const PAYERS = [
  { key: 'axa', payer: 'AXA', label: 'กรุงไทย AXA', needsDate: true, ext: '.xlsx', accept: XLSX, toleranceSatang: 0 },
  { key: 'generali', payer: 'Generali', label: 'Generali', needsDate: false, ext: '.xlsx', accept: XLSX, toleranceSatang: 0 },
  { key: 'aia', payer: 'AIA', label: 'AIA', needsDate: false, ext: '.pdf', accept: '.pdf,application/pdf', toleranceSatang: 0 },
  // ⚠️ 'Gowabi', not 'GoWabi'. The brand spells itself GoWabi; all 521 live billing lines
  // spell the payer Gowabi, and it is the column that has to match, not the logo.
  /* ⚠️ ฿1, and it is the ONLY non-zero tolerance. billing.priceadvance is an int holding
     floor() of the true figure on all 519 live GoWabi lines — ฿785.214 is stored ฿785 while
     the file says ฿785.21. Read exact equality literally and every GoWabi month parks with
     correct rows called wrong. Mirrored from functions/lib/insurerAdapters.js and guarded by
     claimImportPayers.test.mjs. */
  { key: 'gowabi', payer: 'Gowabi', label: 'GoWabi', needsDate: true, ext: '.xlsx', accept: XLSX, toleranceSatang: 100 },
  // ⚠️ `payer` IS 'Prudential', NOT 'BlueVenture'. BlueVenture TPA is the administrator that
  // sends the report; Prudential is the insurer, and it is Prudential that all 440 live
  // billing lines carry in `advancePay`. The label names both because the staff recognise
  // the mail by its sender.
  // ✅ needsDate false: unlike AXA and GoWabi the file carries a real `Paid Date`, constant
  // within a file, so the operator is never asked for a bank value date.
  { key: 'prudential', payer: 'Prudential', label: 'Prudential (BlueVenture TPA)', needsDate: false, ext: '.xls', accept: XLS, toleranceSatang: 0 },
]

/**
 * The payer's declared tolerance in satang, for the browser.
 *
 * ⚠️ Matched on `payer` — the exact string in billing.advancePay, which is what a run carries
 * — and NOT on `key`. 'Gowabi' is the column's spelling on all 521 live lines even though the
 * brand writes GoWabi, and that is the one that has to match.
 * ⚠️ Unknown payer → 0, which is the strict reading. A payer we do not recognise must not be
 * handed a rounding allowance by accident.
 */
export function payerTolerance(payer) {
  const p = String(payer || '').trim().toLowerCase()
  const hit = PAYERS.find((x) => String(x.payer).toLowerCase() === p)
  return Number(hit?.toleranceSatang) || 0
}
